import { useCallback, useEffect, useState } from 'react';
import { getCurrentTnc, acceptTnc, type SprayTncCurrent } from '../services/sprayTnc';

interface UseSprayTncGate {
  loading: boolean;
  needsAcceptance: boolean;
  tnc: SprayTncCurrent | null;
  error: string | null;
  accept: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Fetches the currently active Spray Module T&C and whether the logged-in user
 * has accepted it. `needsAcceptance` is true when a version exists and the
 * user has not yet accepted it — the caller should render the blocking modal.
 */
export function useSprayTncGate(): UseSprayTncGate {
  const [loading, setLoading] = useState(true);
  const [tnc, setTnc] = useState<SprayTncCurrent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCurrentTnc();
      if (res.success) {
        setTnc(res.data);
      } else {
        setError('Failed to load terms of use');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load terms of use');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const accept = useCallback(async (): Promise<boolean> => {
    if (!tnc || tnc.id == null) return false;
    try {
      const res = await acceptTnc(tnc.id);
      if (res.success) {
        setTnc({ ...tnc, hasAccepted: true });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [tnc]);

  const needsAcceptance = !!tnc && tnc.id != null && !tnc.hasAccepted;

  return { loading, needsAcceptance, tnc, error, accept, refresh: load };
}
